// @flow
import * as React from 'react';
import { FormattedMessage, IntlShape, injectIntl } from 'react-intl';
import { Box } from 'grommet';
import { FieldWrapper } from 'speed-js-react-web';
import { withRouter } from 'speed-js-react';
import sidePanelMessages from 'esm-web-pro/messages/sidePanelMessages';
import type { RouterHistory } from 'react-router-dom';
import { ApiFormWithModel } from 'esm-web-pro/containers/forms/FormWithModel';
import { RESOURCES } from 'esm-web-pro/containers/pages/network/Util';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import { bindActionCreators } from 'redux';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import { connect } from 'react-redux';
import BaseSelect from 'esm-web-pro/components/forms/fields/BaseSelect';
import RedButton from 'esm-web-pro/components/layout/RedButton';
import point11Messages from 'esm-web-pro/messages/points11Messages';


type Props = {
    point: Object,
    onClose: () => void,
    onSave: Function,
    updateObject: Function,
    history: RouterHistory,
    match: Object,
    intl: IntlShape,
    editor: BaseEditor,
    setSensitivity: (key: string, sensitivity: boolean) => void,
    id: string,
    map: Object,
};

const mapDispatchToProps = (dispatch) => (
    bindActionCreators(sidePanelActions, dispatch)
);

const Point11AddContent = (props: Props) => {
    const {
        point, onClose, intl: { formatMessage }, editor,
        map: { uuid: mapUUID, is_draft: isDraft },
    } = props;

    const [val, setValue] = React.useState(null);
    const [initial, setInitial] = React.useState(null);
    const initialValues = { infrastructure_provider: point.infrastructure_provider.id };

    const handleChooseContent = (value: string) => {
        if (value === 'rack') {
            setInitial({ ...initialValues, pop: point.id, units: 42 });
        } else {
            setInitial({ ...initialValues, container: point.id });
        }
        setValue(value);
    };

    const contentChoices = React.useMemo(() => {
        if (point.type === 'pop') {
            const result = { ...point.contain_points };

            result.ups = formatMessage(point11Messages.ups);
            result.generator = formatMessage(point11Messages.generator);

            return result;
        }
        return point.contain_points;
    }, [point, formatMessage]);

    const renderContentSelect = () => (
        <FieldWrapper label={(
            <FormattedMessage
                id="content"
                defaultMessage="Content"
            />
        )}
        >
            <BaseSelect
                emptyLabel={(
                    <FormattedMessage
                        id="content-select"
                        defaultMessage="Select content"
                    />
                )}
                choices={contentChoices}
                value={val}
                onChange={handleChooseContent}
            />
        </FieldWrapper>
    );

    const formRest = {};
    if (val) {
        if (val === 'patch_cable') {
            formRest.resourceArguments = [point.uuid];
        }
        if (val === 'odf') {
            formRest.resourceArguments = point.type === 'pop' ? [point.uuid] : [];
            formRest.resourceMethod = point.type === 'pop' ? 'createODF' : 'createNonPoPODF';
            formRest.useResourceMethodForSchema = point.type !== 'pop';
        }
        if (val === 'switch') {
            formRest.resourceArguments = [point.uuid];
            formRest.resourceMethod = 'createSwitch';
            formRest.useResourceMethodForSchema = false;
        }
    }

    const handleSave = (response: Object) => {
        const { onSave } = props;
        const newPoint = { ...point };

        if (val === 'patch_cable') {
            const startTerminationCables = newPoint.connected_cables
                .layers1_2[response.start_termination_point.id].cables;
            let nextCableIndex = Object.keys(startTerminationCables).length;
            newPoint.connected_cables.layers1_2[response.start_termination_point.id]
                .cables[nextCableIndex] = { ...response, sequence: response.patch_sequence };

            const endTerminationCables = newPoint.connected_cables
                .layers1_2[response.end_termination_point.id].cables;
            nextCableIndex = Object.keys(endTerminationCables).length;
            newPoint.connected_cables.layers1_2[response.end_termination_point.id]
                .cables[nextCableIndex] = { ...response, sequence: response.patch_sequence };
        } else if (val === 'rack') {
            const pointObject = {
                id: response.id,
                ref: response.ref,
                sequence: response.sequence,
                uuid: response.uuid,
                identity: response.identity,
            };
            newPoint.empty_racks.push(pointObject);
        } else if (!['ups', 'generator'].includes(val)) {
            const pointObject = {
                id: response.id,
                uuid: response.uuid,
                identity: response.identity,
                type: response.type,
                sequence: response.sequence,
                cables: Object.assign({}, response.cables),
                rack: response.rack,
            };

            if (response.type === 'switch') {
                pointObject.oss_switch_name = response.oss_switch_name;
            }

            if (response.rack) {
                const emptyRack = newPoint.empty_racks?.find((r) => r.id === response.rack.id);
                pointObject.cables = {};
                if (emptyRack) {
                    newPoint.empty_racks.splice(
                        newPoint.empty_racks.indexOf(emptyRack), 1,
                    );
                }
            }
            if (response.container) {
                newPoint.content_count = response.container.content_count;
                newPoint.content_data = response.container.content_data;
                newPoint.connected_cables.layers1_2[response.id] = pointObject;
            }
        }

        onSave(newPoint);
        editor._loadFeatures();
        onClose(true);
    };

    let content = (
        <Box pad={{ horizontal: 'medium', bottom: 'small' }}>
            <RedButton
                label={formatMessage(sidePanelMessages.closeButton)}
                onClick={onClose}
                color="status-error"
            />
        </Box>
    );

    if (['ups', 'generator'].includes(val)) {
        content = (
            <ApiFormWithModel
                objectType={val}
                key={val}
                resourceName="pop.assetForType"
                resourceArguments={[point.uuid, val]}
                excludeFields={['infrastructure_provider', 'container', 'pop', 'map', 'stage', 'ref']}
                initial={initial}
                height="100%"
                onClose={() => onClose()}
                onSave={handleSave}
            />
        );
    } else if (val) {
        content = (
            <ApiFormWithModel
                objectType={val}
                key={val}
                resourceName={RESOURCES[val]}
                excludeFields={['infrastructure_provider', 'container', 'pop', 'map', 'stage', 'ref']}
                initial={initial}
                height="100%"
                onClose={(e) => onClose()}
                onSuccess={handleSave}
                {...formRest}
            />
        );
    }

    return (
        <Box height="100%">
            <Box
                direction="row"
                align="center"
                height="100%"
                overflow="auto"
                style={{ position: 'unset' }}
            >
                <Box alignSelf="stretch" flex justify="between">
                    <Box pad={{ horizontal: 'medium' }}>
                        {renderContentSelect()}
                    </Box>
                    {content}
                </Box>
            </Box>
        </Box>
    );
};

export default connect(null, mapDispatchToProps)(withRouter(injectIntl(Point11AddContent)));
